import { useState, useCallback, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import i18next from 'i18next';
import { useLocation } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import { IoMdClose } from "react-icons/io";

import JobsList from '../../components/UI/Common/Jobs/JobsList/JobsList';
import FormField from '../../components/UI/UpdatedFormUI/FormField/FormField';

import styles from './Jobs.module.css';
import BubbleButton from '../../components/UI/Buttons/BubbleButton/BubbleButton';
import HeroSectionLight from '../../components/Verification/HeroSectionLight/HeroSectionLight';
import { FilterIcons } from '../../assets/images/icons';
import {
    useDebounce,
    useGetCities,
    useGetJobCategoriesHook
} from '../../utils/utilityHooks';
import FormSelectField from '../../components/UI/UpdatedFormUI/FormSelectField/FormSelectField';
import FormRangeField from '../../components/UI/UpdatedFormUI/FormRangeField/FormRangeField';
import FormOptionField from '../../components/UI/UpdatedFormUI/FormOptionField/FormOptionField';
import { useSelector } from 'react-redux';
import axios from 'axios';

function Jobs() {
    const { t } = useTranslation();
    const location = useLocation();
    const query = new URLSearchParams(location.search);
    const initialSortby = query.get('sortby') || 'relevance';
    const [timeFilter, setTimeFilter] = useState([]);
    const initialSearch = query.get('search') || '';
    let initialJobType = query.getAll('job_type') || [];
    let initialJobCategory = query.getAll('category') || [];
    let initialJobLocation = query.getAll('job_location') || [];
    let initialJobCountry = query.getAll('country') || [];
    let initialJobLanguage = query.getAll('language') || [];
    let initialExclusive = query.getAll('exclusiveJobs') || [];
    let initialRoleSkills = query.getAll('role_skills') || [];

    if (initialJobCategory.length) {
        initialJobCategory = initialJobCategory[0].split(',');
    }

    if (initialJobCountry.length) {
        initialJobCountry = initialJobCountry[0].split(',');
    }

    if (initialJobLanguage.length) {
        initialJobLanguage = initialJobLanguage[0].split(',');
    }

    if (initialJobType.length) {
        initialJobType = initialJobType[0].split(',');
    }
    
    if (initialRoleSkills.length) {
        initialRoleSkills = initialRoleSkills[0].split(',');
    }

    const [jobsFoundTotal, setJobsFoundTotal] = useState(0);
    const [sortby, setSortby] = useState(initialSortby);
    const [search, setSearch] = useState(initialSearch);
    const [jobType, setJobType] = useState(initialJobType);
    const [jobCategory, setJobCategory] = useState(initialJobCategory);
    const [jobLocation, setjobLocation] = useState(initialJobLocation);
    const [jobCountry, setJobCountry] = useState(initialJobCountry);
    const [jobLanguage, setJobLanguage] = useState(initialJobLanguage);
    const [roleSkills, setRoleSkills] = useState(initialRoleSkills);
    const [roleSkillsOptions, setRoleSkillsOptions] = useState([]);
    const [allRoleSkills, setAllRoleSkills] = useState([]);
    const [exclusiveJobs, setExclusiveJobs] = useState(initialExclusive[0] == 'true' ? true : false);
    const [updateFilters, setUpdateFilters] = useState();
    const [jobCities, setjobCities] = useState([]);
    const [isCrossAnimating, setIsCrossAnimating] = useState(false);
    const user = useSelector(state => state.user.user);
    const handleUpdateJobsCounters = useCallback((jobs) => {
        setJobsFoundTotal(jobs.total);
    }, []);
    const categoryOptions = [...useGetJobCategoriesHook()];

    // Fetch role-based skills once at component mount
    useEffect(() => {
        const fetchRoleSkills = async () => {
            try {
                const response = await axios.get(`${process.env.REACT_APP_API_URL}job/getRoleBasedSkills`);
                if (response.data) {
                    // Store complete API response for future filtering
                    setAllRoleSkills(response.data);
                    
                    // Get current language
                    const currentLanguage = i18next.language;
                    const isArabic = currentLanguage === 'ar';
                    
                    // Flatten the skills arrays into individual options
                    const flattenedOptions = [];
                    response.data.forEach(item => {
                        // Extract skills based on language
                        const skills = isArabic ? (item.skills_ar || []) : (item.skills_en || []);
                        const roleTitle = isArabic ? (item.label_ar || item.label_en) : (item.label_en || '');
                        
                        // Add each skill as a separate option
                        skills.forEach(skill => {
                            if (skill && skill.trim()) {
                                flattenedOptions.push({
                                    value: skill,
                                    label: skill,
                                    roleTitle: roleTitle,
                                    roleId: item.id
                                });
                            }
                        });
                    });
                    
                    // Remove duplicates by skill value
                    const uniqueOptions = Array.from(
                        new Map(flattenedOptions.map(item => [item.value, item])).values()
                    );
                    
                    setRoleSkillsOptions(uniqueOptions);
                }
            } catch (error) {
                console.error('Error fetching role-based skills:', error);
            }
        };
        
        fetchRoleSkills();
    }, [i18next.language]);

    // Filter role skills based on selected category (uses cached data)
    // This useEffect now runs when allRoleSkills changes or language changes
    useEffect(() => {
        // If no cached data, do nothing
        if (allRoleSkills.length === 0) {
            return;
        }

        // Get current language
        const currentLanguage = i18next.language;
        const isArabic = currentLanguage === 'ar';

        // Initialize with all skills
        const allFlattenedOptions = [];
        allRoleSkills.forEach(item => {
            const skills = isArabic ? (item.skills_ar || []) : (item.skills_en || []);
            const roleTitle = isArabic ? (item.label_ar || item.label_en) : (item.label_en || '');
            
            skills.forEach(skill => {
                if (skill && skill.trim()) {
                    allFlattenedOptions.push({
                        value: skill,
                        label: skill,
                        roleTitle: roleTitle,
                        roleId: item.id
                    });
                }
            });
        });
        
        // Remove duplicates
        const allUniqueOptions = Array.from(
            new Map(allFlattenedOptions.map(item => [item.value, item])).values()
        );
        
        setRoleSkillsOptions(allUniqueOptions);
        
    }, [allRoleSkills, i18next.language]);

    const handleSortbyChange = async (value) => {
        await setSortby(value);
        if (value == 'relevance') {
            filters.sortby = value;
            setUpdateFilters(filters)
        }
        else {
            // filters = {}
            Object.keys(filters).forEach(key => delete filters[key]);
            // setSortby('');
            setSearch('');
            setJobType([]);
            setJobCategory([]);
            setjobLocation([]);
            setJobCountry([]);
            setJobLanguage([]);
            setRoleSkills([]);

            // Directly reset role skills options from cached data for better performance
            if (allRoleSkills.length > 0) {
                // Get current language
                const currentLanguage = i18next.language;
                const isArabic = currentLanguage === 'ar';
                
                const allFlattenedOptions = [];
                allRoleSkills.forEach(item => {
                    const skills = isArabic ? (item.skills_ar || []) : (item.skills_en || []);
                    const roleTitle = isArabic ? (item.label_ar || item.label_en) : (item.label_en || '');
                    
                    skills.forEach(skill => {
                        if (skill && skill.trim()) {
                            allFlattenedOptions.push({
                                value: skill,
                                label: skill,
                                roleTitle: roleTitle,
                                roleId: item.id
                            });
                        }
                    });
                });
                
                // Remove duplicates
                const allUniqueOptions = Array.from(
                    new Map(allFlattenedOptions.map(item => [item.value, item])).values()
                );
                
                setRoleSkillsOptions(allUniqueOptions);
            }

            setExclusiveJobs(false);
            setUpdateFilters({});
            setjobCities([]);
            setIsCrossAnimating(false);
            filters.sortby = 'newest';

            setUpdateFilters(filters)
        }
    }

    const handleFilterSearchChange = (value) => {
        setSearch(value.target.value);
    }

    const handleFilterChange = (value) => {
        switch (value.target.name) {
            case 'filter_job_type':
                setJobType(value.target.value);
                break;
            case 'filter_city':
                setjobCities(value.target.value);
                break;
            case 'filter_category':
                const newCategories = value.target.value;
                setJobCategory(newCategories);
                
                // Direct filtering for better performance
                if (allRoleSkills.length > 0) {
                    // Get current language
                    const currentLanguage = i18next.language;
                    const isArabic = currentLanguage === 'ar';
                    
                    // If no categories selected, show all skills
                    if (newCategories.length === 0) {
                        const allFlattenedOptions = [];
                        allRoleSkills.forEach(item => {
                            const skills = isArabic ? (item.skills_ar || []) : (item.skills_en || []);
                            const roleTitle = isArabic ? (item.label_ar || item.label_en) : (item.label_en || '');
                            
                            skills.forEach(skill => {
                                if (skill && skill.trim()) {
                                    allFlattenedOptions.push({
                                        value: skill,
                                        label: skill,
                                        roleTitle: roleTitle,
                                        roleId: item.id
                                    });
                                }
                            });
                        });
                        
                        // Remove duplicates
                        const allUniqueOptions = Array.from(
                            new Map(allFlattenedOptions.map(item => [item.value, item])).values()
                        );
                        
                        setRoleSkillsOptions(allUniqueOptions);
                    } else {
                        // Filter by category IDs
                        const filteredRoles = allRoleSkills.filter(item => {
                            return item.categories && item.categories.some(catId => 
                                newCategories.includes(catId)
                            );
                        });
                        
                        // If no direct matches, try by category name
                        let rolesToUse = filteredRoles;
                        if (filteredRoles.length === 0) {
                            const selectedCategoryNames = newCategories.map(catId => {
                                const category = categoryOptions.find(cat => cat.value === catId);
                                return category ? (isArabic ? category.labelar : category.labelen).toLowerCase() : '';
                            }).filter(name => name !== '');
                            
                            if (selectedCategoryNames.length > 0) {
                                rolesToUse = allRoleSkills.filter(item => {
                                    const titleLower = (isArabic ? item.title_ar : item.value).toLowerCase();
                                    return selectedCategoryNames.some(category => 
                                        titleLower.includes(category)
                                    );
                                });
                            }
                        }
                        
                        // Extract and flatten skills
                        const flattenedOptions = [];
                        rolesToUse.forEach(item => {
                            const skills = isArabic ? (item.skills_ar || []) : (item.skills_en || []);
                            const roleTitle = isArabic ? (item.label_ar || item.label_en) : (item.label_en || '');
                            
                            skills.forEach(skill => {
                                if (skill && skill.trim()) {
                                    flattenedOptions.push({
                                        value: skill,
                                        label: skill,
                                        roleTitle: roleTitle,
                                        roleId: item.id
                                    });
                                }
                            });
                        });
                        
                        // Remove duplicates
                        const uniqueOptions = Array.from(
                            new Map(flattenedOptions.map(item => [item.value, item])).values()
                        );
                        
                        if (uniqueOptions.length > 0) {
                            setRoleSkillsOptions(uniqueOptions);
                        } else {
                            // Fallback to all skills if no matches
                            const allFlattenedOptions = [];
                            allRoleSkills.forEach(item => {
                                const skills = isArabic ? (item.skills_ar || []) : (item.skills_en || []);
                                const roleTitle = isArabic ? (item.label_ar || item.label_en) : (item.label_en || '');
                                
                                skills.forEach(skill => {
                                    if (skill && skill.trim()) {
                                        allFlattenedOptions.push({
                                            value: skill,
                                            label: skill,
                                            roleTitle: roleTitle,
                                            roleId: item.id
                                        });
                                    }
                                });
                            });
                            
                            const allUniqueOptions = Array.from(
                                new Map(allFlattenedOptions.map(item => [item.value, item])).values()
                            );
                            
                            setRoleSkillsOptions(allUniqueOptions);
                        }
                    }
                }
                break;
            case 'filter_location':
                setjobLocation(value.target.value);
                break;
            case 'filter_country':
                setJobCountry(value.target.value);
                break;
            case 'filter_language':
                setJobLanguage(value.target.value);
                break;
            case 'filter_role_skills':
                setRoleSkills(value.target.value);
                break;
            case 'filter_time':
                const newTimeFilter = timeFilter.includes(value.target.value)
                    ? timeFilter.filter(time => time !== value.target.value)
                    : [...timeFilter, value.target.value];
                setTimeFilter(newTimeFilter);
                break;
            default:
                break;
        }
    }

    const jobLocationOptions = [
        { value: 'remote', label: t('forms.jobLocation.remote') },
        { value: 'in_person', label: t('forms.jobLocation.in_person') },
        { value: 'hybrid', label: t('forms.jobLocation.hybrid') },
    ];
    const jobTypeOptions = [
        { value: 'full_time', label: t('general.UI.full_time') },
        { value: 'part_time', label: t('general.UI.part_time') },
        { value: 'temporary', label: t('forms.workExperience.employment_status.temporary') },
        { value: 'contract', label: t('forms.workExperience.employment_status.contract') },
        { value: 'internship', label: t('forms.workExperience.employment_status.internship') },
        // { value: 'fresher', label: t('forms.workExperience.employment_status.fresher') }
    ];

    const [salaryRange, setsalaryRange] = useState([1, 9000]);
    const filters = {};

    if (sortby !== '') {
        filters.sortby = sortby;
    }
    if (jobType !== 'all') {
        if (jobType.length > 0) {
            filters.job_type = jobType;
        }
    }
    if (search !== '') {
        filters.search = search;
    }
    if (jobCategory.length > 0) {
        filters.category = jobCategory;
    }
    if (jobLocation.length > 0) {
        filters.job_location = jobLocation;
    }
    if (jobCountry.length > 0) {
        filters.country = jobCountry;
    }
    if (jobLanguage.length > 0) {
        filters.language = jobLanguage;
    }
    if (roleSkills.length > 0) {
        filters.role_skills = roleSkills;
    }
    if (exclusiveJobs) {
        filters.exclusiveJobs = exclusiveJobs
    } else {
        filters.exclusiveJobs = false
    }

    const citiesOptions = useGetCities(filters.country);
    if (timeFilter.length > 0) filters.datePosted = timeFilter;
    if (jobCities?.length > 0 && filters?.country?.length > 0) filters.city = jobCities

    const handleFilterSalaryRangeChange = useDebounce((value) => {
        setsalaryRange(value);
    }, 0);

    const timeOptions = [
        { value: '24_hours', label: t('timeOptions.last24Hours') },
        { value: '3_days', label: t('timeOptions.last3Days') },
        { value: '7_days', label: t('timeOptions.last7Days') },
        { value: '14_days', label: t('timeOptions.last14Days') }
    ];

    const getMostRecentTimeFilter = (selectedFilters) => {
        const timeOrder = ['24_hours', '3_days', '7_days', '14_days'];
        let mostRecentIndex = -1;
        let mostRecentValue = null;

        selectedFilters.forEach(filter => {
            const currentIndex = timeOrder.indexOf(filter);
            if (currentIndex > mostRecentIndex) {
                mostRecentIndex = currentIndex;
                mostRecentValue = filter;
            }
        });

        return mostRecentValue;
    };

    const handleApplyFilters = () => {
        filters.exclusiveJobs = exclusiveJobs
        filters.minimum_salary = salaryRange[0]
        filters.maximum_salary = salaryRange[1]
        if (timeFilter.length > 0) filters.datePosted = getMostRecentTimeFilter(timeFilter)
        if (jobCities?.length > 0 && filters?.country?.length > 0) filters.city = jobCities
        filters.sortby = 'relevance';
        setSortby('relevance');
        setUpdateFilters(filters)


        if(filters?.country?.length>0){
            const country= {
                label: t(`general.country.${filters?.country}`),
                value: filters?.country,
                flag: filters?.country
            }
            localStorage.setItem('selectedCountry', JSON.stringify(country));
        }else{
            localStorage.removeItem('selectedCountry');

        }






        if (isFixed) {
        } else {
            const target = document.querySelector('.scrollJobCard')
            if (target) {
                window.scrollTo({
                    top: target.offsetTop, behavior: 'smooth'
                });
            }
        }
    }
    const recentJobs = location.state?.job; // Access the passed job

    useEffect(() => {
        if (recentJobs) { }
        else {
            setUpdateFilters(filters)
        }
    }, [])

    const [toggleFilter, setToggleFilter] = useState(false)
    const handleToggleFilter = () => {
        if (toggleFilter) {
            setIsCrossAnimating(true);
            setTimeout(() => {
                setToggleFilter(!toggleFilter);
                setIsCrossAnimating(false);
            }, 300);
        } else {
            setToggleFilter(!toggleFilter);
        }
    };
    const [isFixed, setIsFixed] = useState(false);
    const buttonRef = useRef(null); // Reference for the button
    const [buttonPosition, setButtonPosition] = useState({ x: 0, y: 0 });
    const getButtonPosition = () => {
        if (buttonRef.current) {
            const rect = buttonRef.current.getBoundingClientRect();
            const container = document.querySelector(`.${styles.sidebar}`);
            const containerWidth = container.offsetWidth; // Gets the width of the container
            setButtonPosition({ x: rect.left, y: rect.top, width: containerWidth });
        }
    };
    useEffect(() => {
        getButtonPosition();
    }, [])
    useEffect(() => {
        const handleScroll = () => {
            if (buttonRef.current) {
                let scrollY = window.scrollY
                if (scrollY < (buttonPosition.y / 3)) {
                    setIsFixed(true);
                }
                else {
                    setIsFixed(false);
                }
            }
        };

        // Add scroll event listener
        window.addEventListener('scroll', handleScroll);

        // Cleanup event listener
        return () => {
            window.removeEventListener('scroll', handleScroll);
        };
    }, [buttonPosition]);
    const sortbyOptions = [
        { value: 'relevance', label: t('jobs.relevance') },
        { value: 'newest', label: t('jobs.newest') },
        { value: 'oldest', label: t('jobs.oldest') },
        { value: 'salary', label: t('jobs.salary') }
    ];

    return (
        <div className={styles.wrapper}>
            <Helmet>
                <title>FindCV - Jobs</title>
            </Helmet>
            {/* <h1>{t('jobs.title')}</h1> */}
            <HeroSectionLight
                title={t('jobs.title')}
                desc={t('jobs.findNextJob')} />
            <div className={styles.content}>
                {/* <div className={styles.i1}></div>
                    <div className={styles.i2}></div>
                    <div className={styles.i3}></div>
                    <div className={styles.i4}></div> */}

                <div className={styles.col} >
                    <div className={toggleFilter ? `${styles.sidebar}` : `${styles.sidebar} ${styles.removeMargin}`}
                    >
                        <div
                            className={toggleFilter ? styles.filterHeader : `${styles.filterHeader} ${styles.removeMargin}`}
                            style={{ cursor: "default" }}
                        >
                            <p className={styles.filterHeading}>{t('jobs.filters')}</p>

                            {/* Cross Icon */}
                            {toggleFilter && (
                                <IoMdClose
                                    onClick={handleToggleFilter}
                                    size={20}
                                    className={`${styles.filterIcon} ${isCrossAnimating ? styles.spinReverse : ''}`}
                                />
                            )}

                            {/* Filter Icon */}
                            {!toggleFilter && (
                                <img
                                    src={FilterIcons}
                                    onClick={handleToggleFilter}
                                    alt="Filter icon"
                                    className={`${styles.filterIcon} ${toggleFilter ? styles.fadeOut : styles.fadeIn}`}
                                />
                            )}
                        </div>
                        <form className={toggleFilter ? `` : `${styles.formHide}`} >
                            <div>
                                <FormField
                                    name="filter_search"
                                    label={t('jobs.search')}
                                    placeholder={t('jobs.searchPlaceHolder')}
                                    type="text"
                                    hasBorder
                                    icon="search"
                                    onChange={handleFilterSearchChange}
                                    value={search}
                                />
                            </div>
                            <div>
                                <FormSelectField
                                    name="filter_country"
                                    placeholder={t('general.UI.select')}
                                    label={t('jobs.country')}
                                    type="country"
                                    options={['QA', 'AE', 'SA', 'BH', 'KW', 'OM']}
                                    isMulti
                                    hasBorder
                                    onFormikChange={handleFilterChange}
                                    value={jobCountry}
                                />
                            </div>
                            {filters?.country?.length > 0 && citiesOptions.length > 0 &&
                                <div>
                                    <FormSelectField
                                        name="filter_city"
                                        placeholder={t('general.UI.City')}
                                        label={t('general.UI.City')}
                                        type="default"
                                        options={citiesOptions}
                                        isMulti
                                        hasBorder
                                        onFormikChange={handleFilterChange}
                                        value={jobCities}
                                    />
                                </div>
                            }
                            <div>
                                <FormSelectField
                                    name="filter_location"
                                    placeholder={t('general.UI.select')}
                                    label={t('forms.jobLocation.location')}
                                    type="default"
                                    isMulti
                                    hasBorder
                                    options={jobLocationOptions}
                                    onFormikChange={handleFilterChange}
                                    value={jobLocation}
                                />
                            </div>
                            <div>
                                <FormSelectField
                                    name="filter_category"
                                    placeholder={t('general.UI.select')}
                                    label={t('jobs.jobCategory')}
                                    type="default"
                                    isMulti
                                    hasBorder
                                    options={categoryOptions}
                                    onFormikChange={handleFilterChange}
                                    value={jobCategory}
                                />
                            </div>
                            
                            {/* Role-based Skills Filter */}
                            <div>
                                <FormSelectField
                                    name="filter_role_skills"
                                    placeholder={t('general.UI.select')}
                                    label={t('job_seeker.role_based_skills')}
                                    type="default"
                                    isMulti
                                    hasBorder
                                    options={roleSkillsOptions}
                                    onFormikChange={handleFilterChange}
                                    value={roleSkills}
                                    isGrouped={true}
                                    groupBy="roleTitle"
                                />
                            </div>
                            
                            <div className={styles.checkboxContainer}>
                                {jobTypeOptions.map(option => (
                                    <div key={option.value} className={styles.checkboxWrapper}
                                    >
                                        <input
                                            type="checkbox"
                                            id={`jobType-${option.value}`}
                                            checked={jobType.includes(option.value)}
                                            onChange={() => {
                                                const newJobType = jobType.includes(option.value)
                                                    ? jobType.filter(type => type !== option.value)
                                                    : [...jobType, option.value];
                                                setJobType(newJobType);
                                            }}
                                            className={styles.checkbox}
                                        />
                                        <label htmlFor={`jobType-${option.value}`} className={styles.checkboxLabel}>
                                            {option.label}
                                        </label>
                                    </div>
                                ))}

                            </div>

                            <div>
                                <FormSelectField
                                    name="filter_language"
                                    placeholder={t('general.UI.select')}
                                    label={t('jobs.language')}
                                    type="language"
                                    isMulti
                                    hasBorder
                                    onFormikChange={handleFilterChange}
                                    value={jobLanguage}
                                />
                            </div>

                            <div >
                                <FormRangeField
                                    name="filter_experience"
                                    label={(salaryRange[0] > 0 || salaryRange[1] < 9000) ? `${t('talents_list.salary_range')}: ${salaryRange[0]}-${salaryRange[1]}` : t('talents_list.salary_range')}
                                    type="radio"
                                    dark
                                    min={1}
                                    max={9000}
                                    tipLabelStart={'$' + salaryRange[0]}
                                    tipLabelEnd={'$' + salaryRange[1]}
                                    defaultValue={salaryRange}
                                    allowCross={false}
                                    range
                                    showTooltip
                                    onChange={handleFilterSalaryRangeChange}
                                />
                            </div>
                            <div className={styles.checkboxContainer}>
                                {timeOptions.map(option => (
                                    <div key={option.value} className={styles.checkboxWrapper}>
                                        <input
                                            type="checkbox"
                                            id={`time-${option.value}`}
                                            checked={timeFilter.includes(option.value)}
                                            onChange={(e) => {
                                                const newTimeFilter = e.target.checked
                                                    ? [...timeFilter, option.value]
                                                    : timeFilter.filter(time => time !== option.value);
                                                setTimeFilter(newTimeFilter);
                                            }}
                                            className={styles.checkbox}
                                        />
                                        <label htmlFor={`time-${option.value}`} className={styles.checkboxLabel}>
                                            {option.label}
                                        </label>
                                    </div>
                                ))}
                            </div>
                            <div className={styles.checkboxWrapper} style={{ justifyContent: "flex-start", width: "100%" }}>
                                <input
                                    type="checkbox"
                                    id="exclusive-jobs"
                                    checked={exclusiveJobs}
                                    onChange={() => {
                                        setExclusiveJobs(!exclusiveJobs);
                                    }}
                                    className={styles.checkbox}
                                />
                                <label htmlFor="exclusive-jobs" className={styles.checkboxLabel}>
                                    {t('timeOptions.showOnlyExclusiveJobs')}
                                </label>
                            </div>
                            <div
                                ref={buttonRef}
                                style={{
                                    position: isFixed ? 'fixed' : 'static', // Fixed or normal position
                                    bottom: isFixed ? '0' : 'auto', // Fixed to bottom if out of view
                                    left: isFixed ? `${buttonPosition.x}px` : 'auto', // Use original left position
                                    zIndex: 1000, // Ensure the button stays on top
                                    width: buttonPosition.width,
                                    transition: 'all 0.3s ease', // Smooth transition
                                }}
                            >
                                <BubbleButton
                                    onClick={handleApplyFilters}
                                    dark
                                    headerDarkButton
                                    style={{ width: "100%", margin: "20px 0" }}
                                >
                                    {t('talents_list.apply_button')}
                                </BubbleButton>
                            </div>

                        </form>
                    </div>

                </div>

                <div className={`${styles.col} pagination_scroll_target`}>
                    <JobsList
                        per_page={10}
                        handleSortbyChange={handleSortbyChange}
                        initialSortBy={filters.sortby}
                        filters={updateFilters}
                        onFetchJobs={handleUpdateJobsCounters}
                    />
                </div>

            </div>

        </div >
    );
}

export default Jobs;